import React from 'react';
import { Lock, LogIn, X } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

function getPromptCopy(accessState, product) {
  if (accessState === 'auth_loading' || accessState === 'subscription_loading') {
    return {
      title: `Checking access to ${product.displayName}`,
      body: 'Just a moment while we confirm your sign-in and plan.',
      primaryLabel: 'Checking access...',
      mode: 'loading',
    };
  }

  if (accessState === 'signed_out') {
    return {
      title: product.signInTitle,
      body: product.signInBody,
      primaryLabel: product.signInCtaLabel,
      mode: 'sign_in',
    };
  }

  return {
    title: product.upgradeTitle,
    body: product.upgradeBody,
    primaryLabel: product.upgradeCtaLabel,
    mode: 'upgrade',
  };
}

export default function PremiumAccessPrompt({
  open,
  variant = 'dialog',
  accessState,
  product,
  onClose,
  onSignIn,
  onUpgrade,
  onDemo,
  demoLabel,
}) {
  if (!open) return null;

  const copy = getPromptCopy(accessState, product);
  const isDialog = variant === 'dialog';

  const card = (
    <div className="relative w-full max-w-lg rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-2xl shadow-slate-300/30">
      {isDialog && onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
          aria-label="Close access prompt"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      ) : null}

      <div className="mb-5 inline-flex rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400 p-3 text-white shadow-lg">
        {copy.mode === 'loading' ? (
          <LoadingSpinner tone="onPrimary" size="sm" className="h-6 w-6" />
        ) : copy.mode === 'sign_in' ? (
          <LogIn className="h-6 w-6" strokeWidth={2} />
        ) : (
          <Lock className="h-6 w-6" strokeWidth={2} />
        )}
      </div>

      <h2 className="text-2xl font-bold tracking-tight text-slate-900">{copy.title}</h2>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{copy.body}</p>

      {copy.mode === 'upgrade' && product.includedIn ? (
        <p className="mt-3 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800">
          {product.includedIn}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        {copy.mode === 'loading' ? (
          <button
            type="button"
            disabled
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-500 sm:w-auto"
          >
            <LoadingSpinner size="sm" />
            {copy.primaryLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={copy.mode === 'sign_in' ? onSignIn : onUpgrade}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:shadow-lg sm:w-auto"
          >
            {copy.primaryLabel}
          </button>
        )}

        {copy.mode !== 'loading' && onDemo ? (
          <button
            type="button"
            onClick={onDemo}
            className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 sm:w-auto"
          >
            {demoLabel || 'See demo'}
          </button>
        ) : null}
      </div>

      {copy.mode === 'upgrade' ? (
        <p className="mt-4 text-xs text-slate-500">
          Signed in users can still preview demos. Full launch requires an active plan that includes this product.
        </p>
      ) : null}
    </div>
  );

  if (variant === 'page') {
    return (
      <div className="min-h-[70vh] bg-gradient-to-br from-blue-50 via-white to-slate-100 px-4 py-16 sm:px-6">
        <div className="mx-auto flex min-h-[55vh] max-w-5xl items-center justify-center">
          {card}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-sm">
      {card}
    </div>
  );
}
